import { useEffect, useState } from 'react';
import { Save, Globe } from 'lucide-react';
import { seoApi } from '../api/cms';
import { defaultSeoPages } from '../api/defaults';
import type { SeoPage } from '../types';

export default function SeoPage() {
  const [pages, setPages] = useState<SeoPage[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    seoApi.list().then(setPages).catch(() => setPages(defaultSeoPages));
  }, []);

  useEffect(() => { if (pages.length && !selected) setSelected(pages[0].id); }, [pages, selected]);

  const page = pages.find(p => p.id === selected);

  const updatePage = (k: keyof SeoPage, v: string | boolean) => {
    setPages(ps => ps.map(p => p.id === selected ? { ...p, [k]: v } : p));
  };

  const save = async () => {
    if (!page) return;
    setSaving(true);
    try {
      await seoApi.update(page.id, page);
      setSaved(true); setTimeout(() => setSaved(false), 3000);
    } catch { /**/ } finally { setSaving(false); }
  };

  const Input = (props: React.InputHTMLAttributes<HTMLInputElement>) => (
    <input {...props} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#295CA8] transition-colors" />
  );
  const Textarea = (props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) => (
    <textarea {...props} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#295CA8] transition-colors resize-none" />
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">SEO</h1>
          <p className="text-white/40 text-sm mt-1">Meta tags, OG, canonical pentru fiecare pagină.</p>
        </div>
        <div className="flex items-center gap-3">
          {saved && <span className="text-xs text-green-400 bg-green-500/10 px-3 py-1.5 rounded-lg border border-green-500/20">✓ Salvat</span>}
          <button onClick={save} disabled={saving || !page}
            className="inline-flex items-center gap-2 bg-[#295CA8] hover:bg-[#3D72B8] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
            <Save size={16} /> {saving ? 'Se salvează...' : 'Salvează'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Page list */}
        <div className="lg:col-span-1 bg-[#07090f] border border-white/10 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-white/10">
            <span className="text-xs font-semibold text-white/40 uppercase tracking-wide">Pagini</span>
          </div>
          <nav className="divide-y divide-white/5">
            {pages.map(p => (
              <button key={p.id} onClick={() => setSelected(p.id)}
                className={`w-full text-left px-4 py-3 transition-all ${selected === p.id ? 'bg-[#295CA8]/20 text-white' : 'text-white/50 hover:text-white hover:bg-white/[0.02]'}`}>
                <div className="flex items-center gap-2">
                  <Globe size={12} className={selected === p.id ? 'text-[#4ab8ff]' : 'text-white/20'} />
                  <span className="text-xs font-medium truncate">{p.pageName}</span>
                </div>
                <span className="text-xs text-white/20 ml-4 truncate block mt-0.5">{p.path}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Editor */}
        {page ? (
          <div className="lg:col-span-3 bg-[#07090f] border border-white/10 rounded-xl p-6 space-y-5">
            <div className="flex items-center gap-2 pb-4 border-b border-white/10">
              <Globe size={16} className="text-[#4ab8ff]" />
              <span className="font-semibold text-white">{page.pageName}</span>
              <span className="text-xs text-white/30 ml-2">{page.path}</span>
            </div>

            <div>
              <label className="block text-xs text-white/50 mb-1.5 font-medium uppercase tracking-wide">Meta Title</label>
              <Input value={page.title} onChange={e => updatePage('title', e.target.value)} placeholder="Titlu SEO..." maxLength={60} />
              <div className="flex justify-between mt-1">
                <span className="text-xs text-white/20">Recomandat: 50-60 caractere</span>
                <span className={`text-xs ${page.title.length > 60 ? 'text-red-400' : 'text-white/30'}`}>{page.title.length}/60</span>
              </div>
            </div>

            <div>
              <label className="block text-xs text-white/50 mb-1.5 font-medium uppercase tracking-wide">Meta Description</label>
              <Textarea value={page.description} onChange={e => updatePage('description', e.target.value)} rows={3} placeholder="Descriere pagina..." maxLength={160} />
              <div className="flex justify-between mt-1">
                <span className="text-xs text-white/20">Recomandat: 120-160 caractere</span>
                <span className={`text-xs ${page.description.length > 160 ? 'text-red-400' : 'text-white/30'}`}>{page.description.length}/160</span>
              </div>
            </div>

            <div>
              <label className="block text-xs text-white/50 mb-1.5 font-medium uppercase tracking-wide">Keywords</label>
              <Textarea value={page.keywords} onChange={e => updatePage('keywords', e.target.value)} rows={2} placeholder="keyword1, keyword2, keyword3" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-white/50 mb-1.5 font-medium uppercase tracking-wide">OG Title</label>
                <Input value={page.ogTitle} onChange={e => updatePage('ogTitle', e.target.value)} placeholder="Open Graph Title" />
              </div>
              <div>
                <label className="block text-xs text-white/50 mb-1.5 font-medium uppercase tracking-wide">OG Image URL</label>
                <Input value={page.ogImage} onChange={e => updatePage('ogImage', e.target.value)} placeholder="https://..." />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs text-white/50 mb-1.5 font-medium uppercase tracking-wide">OG Description</label>
                <Textarea value={page.ogDescription} onChange={e => updatePage('ogDescription', e.target.value)} rows={2} placeholder="Open Graph Description" />
              </div>
              <div>
                <label className="block text-xs text-white/50 mb-1.5 font-medium uppercase tracking-wide">URL Canonical</label>
                <Input value={page.canonical} onChange={e => updatePage('canonical', e.target.value)} placeholder="https://interpersonal.ro/pagina" />
              </div>
              <div className="flex items-center gap-3">
                <label className="text-xs text-white/50 font-medium uppercase tracking-wide">Noindex</label>
                <button onClick={() => updatePage('noindex', !page.noindex)}
                  className={`w-10 h-5 rounded-full transition-colors ${page.noindex ? 'bg-red-500' : 'bg-white/10'}`}>
                  <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform mt-0.5 ${page.noindex ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </button>
                <span className="text-xs text-white/30">{page.noindex ? 'Nu indexat' : 'Indexat'}</span>
              </div>
            </div>

            {/* Preview card */}
            <div className="bg-white/[0.02] border border-white/10 rounded-lg p-4">
              <div className="text-xs text-white/30 mb-2 uppercase tracking-wide">Preview Google</div>
              <div className="text-sm text-[#4ab8ff]">{page.title || 'Titlu pagina...'}</div>
              <div className="text-xs text-green-400 mt-0.5">{page.canonical || page.path}</div>
              <div className="text-xs text-white/50 mt-1 line-clamp-2">{page.description || 'Descriere pagina...'}</div>
            </div>
          </div>
        ) : (
          <div className="lg:col-span-3 bg-[#07090f] border border-white/10 rounded-xl flex items-center justify-center h-64 text-white/20 text-sm">
            Selectați o pagină din lista din stânga.
          </div>
        )}
      </div>
    </div>
  );
}
